import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FaSuperpowers, FaUserPlus } from "react-icons/fa";
import {
  SiConvertio,
  SiLibreofficeimpress,
  SiMaterialformkdocs,
  SiSentry,
  SiStatuspage,
  SiSteelseries,
} from "react-icons/si";
import { ImLocation2 } from "react-icons/im";
import { RiToolsFill } from "react-icons/ri";
import { DiMootoolsBadge } from "react-icons/di";
import { VscGroupByRefType } from "react-icons/vsc";
import { TbBrandReason, TbReorder } from "react-icons/tb";
import {
  MdCategory,
  MdMenuOpen,
  MdSignalWifiStatusbar3Bar,
} from "react-icons/md";
import { LuBriefcaseConveyorBelt } from "react-icons/lu";
import { GiFurnace, GiSpinalCoil } from "react-icons/gi";
import { FaPerson, FaPersonBreastfeeding } from "react-icons/fa6";
import { FcDepartment } from "react-icons/fc";
import { HiOutlineShieldCheck } from "react-icons/hi2";

const masterSections = [
  {
    category: "Die Management",
    items: [
      {
        title: "Die Status",
        route: "/master/die-management/die-status",
        icon: SiStatuspage,
        color: "text-blue-600",
      },
      {
        title: "Die Supplier",
        route: "/master/die-management/supplier-die",
        icon: FaSuperpowers,
        color: "text-indigo-600",
      },
      {
        title: "Location",
        route: "/master/die-management/location",
        icon: ImLocation2,
        color: "text-emerald-600",
      },
      {
        title: "Tooling Size",
        route: "/master/die-management/toolingSize",
        icon: RiToolsFill,
        color: "text-orange-600",
      },
      {
        title: "Tooling-Type",
        route: "/master/tooling-type",
        icon: DiMootoolsBadge,
        color: "text-cyan-600",
      },
      {
        title: "Die Type",
        route: "/master/die-management/dieType",
        icon: VscGroupByRefType,
        color: "text-violet-600",
      },
      {
        title: "DieUnload Reason",
        route: "/master/dieUnloadReason",
        icon: TbBrandReason,
        color: "text-rose-600",
      },
      {
        title: "Tooling-Entry",
        route: "/master/tooling-Entry",
        icon: SiSentry,
        color: "text-red-500",
      },
    ],
  },
  {
    category: "Production & Sales",
    items: [
      {
        title: "Customer",
        route: "/master/sales-customer",
        icon: FaPerson,
        color: "text-teal-600",
      },
      {
        title: "Alloy",
        route: "/master/sales-orders/alloy",
        icon: TbReorder,
        color: "text-amber-600",
      },
      {
        title: "Order Status",
        route: "/master/sales-orders/order-status",
        icon: MdSignalWifiStatusbar3Bar,
        color: "text-blue-500",
      },
      {
        title: "Unit Conversion",
        route: "/master/sales-orders/unit-conversion",
        icon: SiConvertio,
        color: "text-purple-600",
      },
      {
        title: "Spectro",
        route: "/master/spectro",
        icon: LuBriefcaseConveyorBelt,
        color: "text-slate-600",
      },
      {
        title: "Furnace",
        route: "/master/furnace",
        icon: GiFurnace,
        color: "text-red-600",
      },
      {
        title: "Series",
        route: "/master/series",
        icon: SiSteelseries,
        color: "text-gray-700",
      },
      {
        title: "Press",
        route: "/master/press",
        icon: SiLibreofficeimpress,
        color: "text-sky-600",
      },
    ],
  },
  {
    category: "General Masters",
    items: [
      {
        title: "Raw Material",
        route: "/master/raw-material",
        icon: SiMaterialformkdocs,
        color: "text-lime-700",
      },
      {
        title: "Department",
        route: "/master/department",
        icon: FcDepartment,
        color: "text-blue-700",
      },
      {
        title: "Incharge",
        route: "/master/incharge",
        icon: FaPersonBreastfeeding,
        color: "text-pink-600",
      },
      {
        title: "Category",
        route: "/master/category",
        icon: MdCategory,
        color: "text-orange-500",
      },
      {
        title: "Nalco",
        route: "/master/settings/nalco-master",
        icon: GiSpinalCoil,
        color: "text-green-600",
      },
    ],
  },
  {
    category: "System Settings",
    items: [
      {
        title: "User",
        route: "/master/settings/users",
        icon: FaUserPlus,
        color: "text-indigo-700",
      },
      {
        title: "Menu Master",
        route: "/master/settings/menu-master",
        icon: MdMenuOpen,
        color: "text-slate-700",
      },
    ],
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1 },
};

const iconVariants = {
  hover: {
    scale: 1.2,
    rotate: [0, -10, 10, -10, 0],
    transition: {
      duration: 0.5,
      repeat: Infinity,
      repeatType: "reverse",
    },
  },
};

export default function SetUp() {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col h-full font-plus overflow-hidden">
      <div className="flex-1 overflow-y-auto px-8 py-6 space-y-12">
        {masterSections.map((section) => (
          <div key={section.category} className="space-y-6">
            <div className="flex items-center gap-6">
              <span className="h-6 w-2 bg-primary"></span>
              <h2 className="text-[12px] font-black uppercase tracking-[5px] text-gray-400">
                {section.category}
              </h2>
              <div className="flex-1 h-[1px] bg-gray-200"></div>
            </div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-0 border-l border-t border-gray-200"
            >
              {section.items.map((card) => {
                const Icon = card.icon;
                return (
                  <motion.button
                    key={card.title}
                    variants={itemVariants}
                    onClick={() => navigate(card.route)}
                    whileTap={{ scale: 0.98 }}
                    className="group relative flex flex-col items-center justify-center p-10 bg-white border-r border-b border-gray-200 hover:bg-gray-50 transition-all duration-200 min-h-[180px] overflow-hidden"
                  >
                    {/* Brand Active Indicator */}
                    <div className="absolute top-0 left-0 w-full h-[3px] bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>

                    <div className="p-5 bg-gray-50 border border-gray-100 group-hover:bg-white group-hover:border-primary/20 transition-all mb-5">
                      <Icon
                        className={`text-[24px] ${card.color} transition-colors group-hover:text-primary`}
                      />
                    </div>

                    <h4 className="text-[11px] font-black text-gray-600 uppercase tracking-[2px] group-hover:text-primary text-center leading-relaxed">
                      {card.title}
                    </h4>
                  </motion.button>
                );
              })}
            </motion.div>
          </div>
        ))}
      </div>
    </div>
  );
}

